import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Check, Sparkles, ExternalLink } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBilling } from "@/contexts/BillingContext";
import { BILLING } from "@/constants/testIds";

const SESSION_FLAG = "sux1_promo_shown_v1";
const DELAY_MS = 10000;

/**
 * PromoUpgradeModal
 *
 * A one-off promotional pop-up that mirrors the /billing/upgrade hero.
 * Rules:
 *  - Only shown to logged-in, non-Pro users
 *  - Fires 10 s after the component mounts on the dashboard
 *  - Only once per browser session (sessionStorage flag)
 *  - Fully i18n (FR/EN) and dismissable — the user can still access the app
 */
export const PromoUpgradeModal = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { billing, paymentsBlockedInTWA } = useBilling();
  const [open, setOpen] = useState(false);

  const isPro = !!billing?.is_pro;
  const price = billing?.pro_price_eur ?? 2;
  const priceStr = price.toFixed(2);

  useEffect(() => {
    if (!user) return;
    if (!billing) return;
    if (isPro || billing.has_paid) return;
    if (typeof window === "undefined") return;
    try {
      if (window.sessionStorage.getItem(SESSION_FLAG) === "1") return;
    } catch (_) {
      /* sessionStorage might be blocked — silently proceed */
    }

    const timer = window.setTimeout(() => {
      try {
        window.sessionStorage.setItem(SESSION_FLAG, "1");
      } catch (_) {}
      setOpen(true);
    }, DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [user, billing, isPro]);

  if (!open || isPro) return null;

  const onClose = () => setOpen(false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#002FA7]/70 backdrop-blur-sm p-4"
      data-testid={BILLING.promoModal}
      onClick={onClose}
    >
      <div
        className="relative bg-card border border-[#002FA7] max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid={BILLING.promoModalClose}
          className="absolute top-3 right-3 p-2 hover:bg-secondary transition-colors z-10"
          aria-label={t("billing.close_modal")}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Hero — bleu roi, matches /billing/upgrade */}
        <div className="border-b border-border bg-[#002FA7] text-white p-8">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-[#002FA7]" />
            <span className="sux-marker-light">
              {t("billing.upgrade_page_marker")}
            </span>
          </div>
          <h2
            className="text-3xl md:text-4xl font-black leading-[0.95] mb-3"
            style={{ fontFamily: "Outfit" }}
          >
            {t("billing.upgrade_page_title_a")}
            <br />
            {t("billing.upgrade_page_title_b")}
          </h2>
          <div className="flex items-end gap-2 mt-6">
            <span
              className="text-5xl font-black tracking-tight text-[#002FA7]"
              style={{ fontFamily: "Outfit" }}
            >
              €{priceStr}
            </span>
            <span className="sux-marker-light mb-2">
              {t("billing.upgrade_paid_once")}
            </span>
          </div>
        </div>

        {/* Body — feature list + CTAs */}
        <div className="p-8">
          <ul className="space-y-2.5 mb-6 text-sm">
            {[
              t("billing.paywall_features_unlimited"),
              t("billing.paywall_features_models"),
              t("billing.paywall_features_share"),
              t("billing.paywall_features_lifetime"),
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/billing/upgrade"
            onClick={onClose}
            data-testid={BILLING.promoModalCta}
            className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs py-4 hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2"
          >
            {paymentsBlockedInTWA ? (
              <ExternalLink className="w-4 h-4" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {t("billing.promo_cta")}
          </Link>

          <button
            onClick={onClose}
            data-testid={BILLING.promoModalDismiss}
            className="w-full text-center font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-foreground mt-4 py-2"
          >
            {t("billing.promo_dismiss")}
          </button>
        </div>
      </div>
    </div>
  );
};
